import type {
  FlexProps,
  IntrinsicSystemProps,
  MotionPropsFor,
} from '@hover/blueprint';
import {
  Box,
  Motion,
  forwardRef,
  motion,
  transition,
  useEventListener,
  useTheme,
} from '@hover/blueprint';
import { useInterval } from '@hover/blueprint/chakra';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';

import { useCalculateSlidePagerTranslation } from './useCalculateSlidePagerTranslation';

const isInteractiveElement = (element: HTMLElement) =>
  ['a', 'button', 'input'].includes(element.tagName.toLowerCase());

interface PageIndicatorProps
  extends MotionPropsFor<IntrinsicSystemProps<'button'>> {
  /**
   * Whether the dot is the active element
   */
  isActive: boolean;
  /**
   * Size of the expanded dot, default is 18
   */
  expandedSize?: number;
  /**
   * Size of the dot, default is 6
   */
  size?: number;
  /**
   * Margin for dots, default is 2
   */
  margin?: number;
}

const PageIndicator = forwardRef<PageIndicatorProps, 'div'>(
  ({ isActive, expandedSize = 18, size = 6, margin = 2, ...props }, ref) => {
    const { colors } = useTheme();

    const idleOutline = '1px solid transparent';
    const focusOutline = `1px solid ${colors.lightAlpha[isActive ? 700 : 600]}`;
    return (
      <motion.button
        _focusVisible={{ outline: focusOutline }}
        _hover={{ bg: 'lightAlpha.700', '& > div': { bg: 'lightAlpha.900' } }}
        animate={{
          width: isActive ? expandedSize : size,
          height: size,
          marginRight: margin,
          marginLeft: margin,
        }}
        bg={isActive ? 'lightAlpha.900' : 'lightAlpha.500'}
        border="none"
        borderRadius="round"
        cursor="pointer"
        height={`${size}px`}
        outline={idleOutline}
        outlineOffset="1px"
        overflow="hidden"
        padding="0"
        ref={ref}
        sx={transition.easing250('background', 'outline')}
        {...props}
      />
    );
  },
);

export interface SliderPagerProps extends Omit<FlexProps, 'children'> {
  /**
   * Number of elements in the carousel
   */
  numberOfElements: number;
  /**
   * Index of the active element
   */
  activeIndex: number;
  /**
   * Maximum number of visible dots
   */
  maxVisibleDots?: number;
  /**
   * Trigger pager animation to right or left on demand
   */
  animate?: 'left' | 'right';
  /**
   * Callback to reset the animate prop
   */
  animateReset?: (value: 'left' | 'right' | undefined) => void;
  /**
   * Interval at which to update the progress indicator during auto-play, default 25
   */
  interval?: number;
  /**
   * Duration between auto-play changes in milliseconds default 5000ms
   */
  duration?: number;
  isPlaying?: boolean;
  /**
   * Whether to reset auto-play progress when `isPlaying` changes
   */
  resetOnPause?: boolean;
  /**
   * Callback when a dot is clicked
   */
  onToggle?: () => void;
  onDotClick: (index: number) => void;
  transitionTimer?: number;
  /**
   * Trigger previous or next slide
   */
  previous: () => void;
  next: () => void;
}

const SlidePager = forwardRef<SliderPagerProps, 'div'>(
  (
    {
      numberOfElements,
      maxVisibleDots,
      activeIndex,
      animate,
      animateReset,
      interval = 25,
      duration = 5000,
      isPlaying = false,
      resetOnPause = true,
      onToggle,
      onDotClick,
      transitionTimer = 500,
      previous,
      next,
      ...props
    },
    ref,
  ) => {
    const [progress, setProgress] = useState(0);
    const [inTransition, setInTransition] = useState(false);

    const dotSize = 6;
    const dotExpandedSize = 18;
    const dotMargin = 2;

    const getTranslationPosition = useCalculateSlidePagerTranslation(
      maxVisibleDots || 0,
      numberOfElements,
      activeIndex,
      dotSize,
      dotMargin,
      dotExpandedSize,
    );

    const reset = useCallback(() => setProgress(0), []);

    useInterval(
      () => {
        const incremented = progress + 1 / (duration / interval);

        setProgress(incremented);

        if (incremented >= 1) {
          next();
          reset();
        }
      },
      isPlaying ? interval : null,
    );

    useEffect(() => {
      if (!isPlaying && resetOnPause) reset();
      if (animate) {
        if (animate === 'left') {
          previous();
        } else {
          next();
        }
        animateReset?.(undefined);
      }
    }, [isPlaying, reset, resetOnPause, animate, previous, next, animateReset]);

    useEventListener('keydown', event => {
      const { target } = event;

      switch (event.key) {
        case 'ArrowLeft':
          if (inTransition) break;
          setInTransition(true);
          setTimeout(() => setInTransition(false), transitionTimer);
          if (isPlaying) reset();
          previous();
          break;
        case 'ArrowRight':
          if (inTransition) break;
          setInTransition(true);
          setTimeout(() => setInTransition(false), transitionTimer);
          if (isPlaying) reset();
          next();
          break;
        case ' ':
          if (target instanceof HTMLElement && isInteractiveElement(target)) {
            break;
          }

          onToggle?.();
          break;
        default:
          break;
      }
    });

    return (
      <Box
        alignItems="center"
        direction="row"
        justifyContent="flex-start"
        maxWidth={maxVisibleDots ? `${maxVisibleDots * dotSize * 2}px` : 'auto'}
        overflow="hidden"
        role="tablist"
        {...props}
        ref={ref}
      >
        <Motion.Firm>
          <AnimatePresence initial={false}>
            <Box
              transform={getTranslationPosition}
              transition="transform 1s ease"
            >
              {Array.from({ length: numberOfElements }).map((_, index) => (
                <PageIndicator
                  expandedSize={dotExpandedSize}
                  isActive={index === activeIndex}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`pager-dot-${index}`}
                  margin={dotMargin}
                  onClick={() => {
                    onDotClick(index);
                  }}
                  size={dotSize}
                />
              ))}
            </Box>
          </AnimatePresence>
        </Motion.Firm>
      </Box>
    );
  },
);

export { SlidePager };
